import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';
import { UploadDialog } from './upload-dialog';
import { FolderForm } from './folder-form';
import { ShareDialog } from './share-dialog';
import { PreviewDialog } from './preview-dialog';
import { FileThumbnail } from './file-thumbnail';
import { fileTypeCategory } from './file-type';
import {
  DeleteForeverIcon,
  DownloadIcon,
  PreviewIcon,
  RestoreIcon,
  ShareIcon,
  TrashIcon,
} from './file-action-icons';

const UNITS = ['B', 'KB', 'MB', 'GB'];

function formatBytes(bytes: number): string {
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${UNITS[unitIndex]}`;
}

/**
 * Mi Nube — a personal drive, not a shared tenant one: everyone sees and
 * organizes only their own folders and files (see `FileService`'s own doc
 * comment on ownership). Browsed one folder at a time via `?folder=<id>`,
 * the same way the URL carries state for the groups map and the growth
 * report rather than local-only state a reload would lose.
 */
export function CloudPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFolderId = searchParams.get('folder');

  const [showTrash, setShowTrash] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [sharingFileId, setSharingFileId] = useState<string | null>(null);
  const [previewingFile, setPreviewingFile] = useState<{
    id: string;
    fileName: string;
    mimeType: string;
  } | null>(null);

  const currentFolder = useQuery({
    queryKey: ['cloud', 'folders', currentFolderId],
    queryFn: ({ signal }) => filesApi.getFolder(currentFolderId!, signal),
    enabled: currentFolderId !== null,
  });

  const folders = useQuery({
    queryKey: ['cloud', 'folders', 'children', currentFolderId, showTrash],
    queryFn: ({ signal }) =>
      filesApi.listFolders(
        { parentFolderId: currentFolderId ?? undefined, includeTrashed: showTrash },
        signal,
      ),
  });

  const files = useQuery({
    queryKey: ['cloud', 'files', currentFolderId, showTrash],
    queryFn: ({ signal }) =>
      filesApi.list(
        { folderId: currentFolderId ?? undefined, includeTrashed: showTrash, limit: 100 },
        signal,
      ),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['cloud'] });
  };

  const download = useMutation({
    mutationFn: async (fileId: string) => {
      const { url } = await filesApi.getDownloadUrl(fileId);
      window.open(url, '_blank', 'noopener');
    },
  });

  const trashFile = useMutation({
    mutationFn: (id: string) => filesApi.archive(id),
    onSuccess: refresh,
  });
  const restoreFile = useMutation({
    mutationFn: (id: string) => filesApi.restoreFile(id),
    onSuccess: refresh,
  });
  const deleteFileForever = useMutation({
    mutationFn: (id: string) => filesApi.deleteFileForever(id),
    onSuccess: refresh,
  });
  const trashFolder = useMutation({
    mutationFn: (id: string) => filesApi.trashFolder(id),
    onSuccess: refresh,
  });
  const restoreFolder = useMutation({
    mutationFn: (id: string) => filesApi.restoreFolder(id),
    onSuccess: refresh,
  });
  const deleteFolderForever = useMutation({
    mutationFn: (id: string) => filesApi.deleteFolderForever(id),
    onSuccess: refresh,
  });

  const openFolder = (id: string | null) => {
    if (id) setSearchParams({ folder: id });
    else setSearchParams({});
  };

  const fileRows = files.data?.data ?? [];
  const folderRows = folders.data ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('files.file.title')}</h1>
          <nav className="back-link" aria-label={t('files.folder.breadcrumb')}>
            <button type="button" className="chip" onClick={() => openFolder(null)}>
              {t('files.folder.root')}
            </button>
            {currentFolder.data?.path.map((crumb) => (
              <span key={crumb.id}>
                {' / '}
                <button type="button" className="chip" onClick={() => openFolder(crumb.id)}>
                  {crumb.name}
                </button>
              </span>
            ))}
            {currentFolder.data && (
              <span>
                {' / '}
                <strong>{currentFolder.data.name}</strong>
              </span>
            )}
          </nav>
        </div>

        <div className="page-actions">
          {can('file.folder.create') && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              onClick={() => setIsCreatingFolder(true)}
            >
              {t('files.folder.create')}
            </button>
          )}
          {can('file.object.create') && (
            <button
              type="button"
              className="btn"
              data-variant="primary"
              onClick={() => setIsUploading(true)}
            >
              {t('files.file.upload')}
            </button>
          )}
        </div>
      </header>

      <div className="filters">
        <label className="field-checkbox">
          <input
            type="checkbox"
            checked={showTrash}
            onChange={(event) => setShowTrash(event.target.checked)}
          />
          {t('files.trash.showInThisFolder')}
        </label>
      </div>

      {(folders.isError || files.isError) && (
        <Alert tone="danger">
          {folders.error instanceof ApiError
            ? t(`errors.${folders.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : files.error instanceof ApiError
              ? t(`errors.${files.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
              : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {(folders.isPending || files.isPending) && <Spinner label={t('common.states.loading')} />}

      {folders.isSuccess && files.isSuccess && folderRows.length === 0 && fileRows.length === 0 && (
        <EmptyState title={t('files.file.empty')} />
      )}

      {folderRows.length > 0 && (
        <ul className="chip-list">
          {folderRows.map((folder) => (
            <li key={folder.id}>
              {folder.trashedAt ? (
                <span className="chip" data-archived>
                  {folder.name}
                  {can('file.folder.update') && (
                    <Button
                      variant="ghost"
                      className="btn-icon"
                      aria-label={t('common.actions.restore')}
                      title={t('common.actions.restore')}
                      onClick={() => restoreFolder.mutate(folder.id)}
                    >
                      <RestoreIcon />
                    </Button>
                  )}
                  {can('file.folder.delete') && (
                    <Button
                      variant="ghost"
                      className="btn-icon"
                      aria-label={t('files.trash.deleteForever')}
                      title={t('files.trash.deleteForever')}
                      onClick={() => deleteFolderForever.mutate(folder.id)}
                    >
                      <DeleteForeverIcon />
                    </Button>
                  )}
                </span>
              ) : (
                <span className="chip">
                  <button type="button" onClick={() => openFolder(folder.id)}>
                    {folder.name} ({folder.folderCount + folder.fileCount})
                  </button>
                  {can('file.folder.delete') && (
                    <Button variant="ghost" onClick={() => trashFolder.mutate(folder.id)}>
                      {t('files.trash.moveToTrash')}
                    </Button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {fileRows.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('files.file.fileName')}</th>
                <th scope="col">{t('files.file.typeColumn')}</th>
                <th scope="col">{t('files.file.size')}</th>
                <th scope="col">{t('files.file.uploadedAt')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {fileRows.map((file) => (
                <tr key={file.id} data-archived={file.archivedAt ? true : undefined}>
                  <td>
                    <span className="cell-with-thumb">
                      <FileThumbnail fileId={file.id} mimeType={file.mimeType} />
                      {file.fileName}
                    </span>
                    {file.shareCount > 0 && (
                      <span className="cell-flag" title={t('files.share.title')}>
                        {' '}
                        · {t('files.share.count', { count: file.shareCount })}
                      </span>
                    )}
                  </td>
                  <td>{t(`files.fileType.${fileTypeCategory(file.mimeType)}`)}</td>
                  <td dir="ltr">{formatBytes(file.sizeBytes)}</td>
                  <td dir="ltr">{formatDateTime(file.createdAt, locale)}</td>
                  <td>
                    {file.archivedAt ? (
                      <>
                        {can('file.object.restore') && (
                          <Button
                            variant="ghost"
                            className="btn-icon"
                            aria-label={t('common.actions.restore')}
                            title={t('common.actions.restore')}
                            onClick={() => restoreFile.mutate(file.id)}
                          >
                            <RestoreIcon />
                          </Button>
                        )}
                        {can('file.object.delete') && (
                          <Button
                            variant="ghost"
                            className="btn-icon"
                            aria-label={t('files.trash.deleteForever')}
                            title={t('files.trash.deleteForever')}
                            onClick={() => deleteFileForever.mutate(file.id)}
                          >
                            <DeleteForeverIcon />
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          className="btn-icon"
                          aria-label={t('files.preview.action')}
                          title={t('files.preview.action')}
                          onClick={() =>
                            setPreviewingFile({
                              id: file.id,
                              fileName: file.fileName,
                              mimeType: file.mimeType,
                            })
                          }
                        >
                          <PreviewIcon />
                        </Button>
                        <Button
                          variant="ghost"
                          className="btn-icon"
                          aria-label={t('files.file.download')}
                          title={t('files.file.download')}
                          loading={download.isPending && download.variables === file.id}
                          onClick={() => download.mutate(file.id)}
                        >
                          <DownloadIcon />
                        </Button>
                        {can('file.share.manage') && (
                          <Button
                            variant="ghost"
                            className="btn-icon"
                            aria-label={t('files.share.create')}
                            title={t('files.share.create')}
                            onClick={() => setSharingFileId(file.id)}
                          >
                            <ShareIcon />
                          </Button>
                        )}
                        {can('file.object.archive') && (
                          <Button
                            variant="ghost"
                            className="btn-icon"
                            aria-label={t('files.trash.moveToTrash')}
                            title={t('files.trash.moveToTrash')}
                            onClick={() => trashFile.mutate(file.id)}
                          >
                            <TrashIcon />
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreatingFolder && (
        <FolderForm
          parentFolderId={currentFolderId}
          onClose={() => setIsCreatingFolder(false)}
          onCreated={() => {
            setIsCreatingFolder(false);
            refresh();
          }}
        />
      )}

      {isUploading && (
        <UploadDialog
          folderId={currentFolderId}
          onClose={() => setIsUploading(false)}
          onUploaded={() => {
            setIsUploading(false);
            refresh();
          }}
        />
      )}

      {sharingFileId && (
        <ShareDialog fileId={sharingFileId} onClose={() => setSharingFileId(null)} />
      )}

      {previewingFile && (
        <PreviewDialog
          fileId={previewingFile.id}
          fileName={previewingFile.fileName}
          mimeType={previewingFile.mimeType}
          onClose={() => setPreviewingFile(null)}
        />
      )}
    </div>
  );
}
